import React, { FC } from 'react';
import { LuUsers } from 'react-icons/lu';
import ConfigAccordion from '@/components/elements/ConfigAccordion';
import ConfigInput from '@/components/elements/ConfigInput';
import ConfigDropdown from '@/components/elements/ConfigDropdown';
import {
  PROVIDER_CONFIG_PROPS,
  AUTH_TYPE_OPTIONS,
  VERSION_OPTIONS,
} from '@/constant/provider/provider';
import ConfigToggle from '@/components/elements/ConfigToggle';
import {
  PROVIDER_CONFIG_FIELD_ICONS,
  PROVIDER_SERVICE_ICONS,
} from '@/constant/provider/providerIcons';
import { PROVIDER_TEST_IDS } from '@/constant/testIds/provider.testIds';
import { ServiceConfig } from '../config.types';
import { CONFIG_COLORS } from '../configColors.constants';
import VdiTypesMultiSelect from '../VDITypesMultiSelect';
import { isValidUrl } from '../urlValidator';
import { tid } from '@/utils/testId';

interface ClientsConfigSectionProps {
  config: ServiceConfig;
  isOpen: boolean;
  onToggle: () => void;
  onChange: (config: ServiceConfig) => void;
  error?: string;
}

const ClientsConfigSection: FC<ClientsConfigSectionProps> = ({
  config,
  isOpen,
  onToggle,
  onChange,
  error,
}) => {
  const colors = CONFIG_COLORS.CLIENTS;

  const hostError =
    config.ENABLED && config.AUTH.AUTH_TYPE && config.AUTH.AUTH_TYPE !== 'none'
      ? !config.AUTH.HOST
        ? 'Host URL is required'
        : !isValidUrl(config.AUTH.HOST)
          ? 'Must be a valid URL (e.g. https://api.example.com)'
          : undefined
      : undefined;

  const handleEnabledChange = (enabled: boolean) => {
    if (!enabled) {
      onChange({ ...config, ENABLED: false, GET_STATE: false });
    } else {
      const newAuthType = config.AUTH.AUTH_TYPE || 'none';
      const newVersion = config.VERSION || 'none';
      const newHost =
        config.AUTH.HOST || (newAuthType === 'none' ? 'https://' : '');
      onChange({
        ...config,
        ENABLED: true,
        AUTH: { HOST: newHost, AUTH_TYPE: newAuthType },
        VERSION: newVersion,
      });
    }
  };

  const handleAuthTypeChange = (value: string) => {
    const newHost =
      value === 'none' && !config.AUTH.HOST ? 'https://' : config.AUTH.HOST;
    onChange({ ...config, AUTH: { HOST: newHost, AUTH_TYPE: value } });
  };

  return (
    <ConfigAccordion
      index={3}
      isOpen={isOpen}
      onToggle={onToggle}
      title={PROVIDER_CONFIG_PROPS.SECTIONS.CLIENTS_TITLE}
      enabled={config.ENABLED}
      onEnabledChange={handleEnabledChange}
      iconColor={colors.accordionIcon}
      titleColor={colors.accordionTitle}
      customColor={colors.switchColor}
      hasError={!!(error || hostError)}
      customIcon={<PROVIDER_SERVICE_ICONS.CLIENTS className="w-5 h-5" />}
      testId={tid(PROVIDER_TEST_IDS.CRUD_CLIENTS_ACCORDION)}
    >
      <div className="space-y-4">
        <ConfigInput
          label={PROVIDER_CONFIG_PROPS.FIELDS.HOST_URL}
          value={config.AUTH.HOST}
          onChange={(e) =>
            onChange({
              ...config,
              AUTH: { ...config.AUTH, HOST: e.target.value },
            })
          }
          placeholder="https://api.example.com"
          focusRingColor={colors.focusRingColor}
          iconColor={colors.iconColor}
          required={
            config.ENABLED &&
            !!config.AUTH.AUTH_TYPE &&
            config.AUTH.AUTH_TYPE !== 'none'
          }
          error={hostError}
          icon={PROVIDER_CONFIG_FIELD_ICONS.HOST}
          dataTestId={tid(PROVIDER_TEST_IDS.CRUD_CLIENTS_HOST_INPUT)}
          disabled={!config.ENABLED}
        />
        <div className="grid grid-cols-1 gap-4 items-start sm:grid-cols-2 xl:grid-cols-3">
          <ConfigDropdown
            label={PROVIDER_CONFIG_PROPS.FIELDS.AUTH_TYPE}
            value={config.AUTH.AUTH_TYPE}
            options={AUTH_TYPE_OPTIONS}
            onChange={handleAuthTypeChange}
            placeholder={PROVIDER_CONFIG_PROPS.FIELDS.AUTH_TYPE_PLACEHOLDER}
            activeBorderClass={colors.toggleCheckedBorder}
            iconColor={colors.iconColor}
            itemSelectedBg={colors.itemSelectedBg}
            accentColor={colors.iconColor}
            focusBorderColor={colors.accentFocusBorder}
            selectedGradient={colors.dropdownSelectedGradient}
            icon={PROVIDER_CONFIG_FIELD_ICONS.AUTH_TYPE}
            dataTestId={tid(PROVIDER_TEST_IDS.CRUD_CLIENTS_AUTH_TYPE_SELECT)}
            disabled={!config.ENABLED}
          />
          <ConfigDropdown
            label={PROVIDER_CONFIG_PROPS.FIELDS.VERSION}
            value={config.VERSION}
            options={VERSION_OPTIONS}
            onChange={(value) => onChange({ ...config, VERSION: value })}
            placeholder={PROVIDER_CONFIG_PROPS.FIELDS.VERSION_PLACEHOLDER}
            activeBorderClass={colors.toggleCheckedBorder}
            iconColor={colors.iconColor}
            itemSelectedBg={colors.itemSelectedBg}
            accentColor={colors.iconColor}
            focusBorderColor={colors.accentFocusBorder}
            selectedGradient={colors.dropdownSelectedGradient}
            icon={PROVIDER_CONFIG_FIELD_ICONS.VERSION}
            dataTestId={tid(PROVIDER_TEST_IDS.CRUD_CLIENTS_VERSION_SELECT)}
            disabled={!config.ENABLED}
          />
          <ConfigToggle
            label={PROVIDER_CONFIG_PROPS.FIELDS.GET_STATE}
            checked={config.GET_STATE}
            onChange={(checked) => onChange({ ...config, GET_STATE: checked })}
            description={
              config.GET_STATE
                ? PROVIDER_CONFIG_PROPS.FIELDS.ENABLED
                : PROVIDER_CONFIG_PROPS.FIELDS.DISABLED
            }
            toggleCheckedBg={colors.toggleCheckedBg}
            toggleCheckedBorder={colors.toggleCheckedBorder}
            toggleCheckedText={colors.toggleCheckedText}
            toggleCheckedIconBg={colors.toggleCheckedIconBg}
            customColor={colors.switchColor}
            icon={<PROVIDER_CONFIG_FIELD_ICONS.GET_STATE className="w-4 h-4" />}
            testId={tid(PROVIDER_TEST_IDS.CRUD_CLIENTS_GET_STATE_TOGGLE)}
            disabled={!config.ENABLED}
          />
        </div>

        {/* VDI Types Section */}
        {config.VDI_TYPES && (
          <VdiTypesMultiSelect
            selectedTypes={config.VDI_TYPES}
            onChange={(types) => onChange({ ...config, VDI_TYPES: types })}
            theme="green"
            required={config.ENABLED}
            error={config.ENABLED ? error : undefined}
            testIdPrefix={tid(PROVIDER_TEST_IDS.CRUD_CLIENTS_VDI_TYPES_PREFIX)}
            disabled={!config.ENABLED}
          />
        )}
      </div>
    </ConfigAccordion>
  );
};

export default ClientsConfigSection;
